import React, { useState, useEffect } from 'react';
import { Card, CardBody, Row, Col, Form, Button, Nav, Tab, Spinner, Alert } from 'react-bootstrap';
import settingsRepository from '../../services/repositories/settings.repository';

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('header');
  
  // Header settings
  const [headerSettings, setHeaderSettings] = useState({
    header_logo: '',
    header_logo_black: '',
    header_phone1: '',
    header_phone2: '',
  });
  
  // Footer settings
  const [footerSettings, setFooterSettings] = useState({
    footer_logo: '',
    footer_address: '',
    footer_phone1: '',
    footer_phone2: '',
    footer_email: '',
    footer_facebook: '',
    footer_instagram: '',
    footer_twitter: '',
    footer_whatsapp: '',
    footer_copyright: '',
    // Branch addresses - Fes (2 branches) and Casa (1 branch)
    branch_fes1_name: '',
    branch_fes1_address: '',
    branch_fes1_phone: '',
    branch_fes2_name: '',
    branch_fes2_address: '',
    branch_fes2_phone: '',
    branch_casa_name: '',
    branch_casa_address: '',
    branch_casa_phone: '',
    // Region specific contact info
    footer_phone_fes: '',
    footer_phone_casa: '',
    footer_email_fes: '',
    footer_email_casa: '',
  });

  // Footer links
  const [footerInfoLinks, setFooterInfoLinks] = useState([]);
  const [footerSupportLinks, setFooterSupportLinks] = useState([]);
  const [footerAccountLinks, setFooterAccountLinks] = useState([]);
  const [footerGroceryLinks, setFooterGroceryLinks] = useState([]);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsRepository.getAll();
      if (response.success) {
        const data = response.data;
        
        // Set header settings
        setHeaderSettings({
          header_logo: data.header_logo || '',
          header_logo_black: data.header_logo_black || '',
          header_phone1: data.header_phone1 || '',
          header_phone2: data.header_phone2 || '',
        });
        
        // Set footer settings
        setFooterSettings({
          footer_logo: data.footer_logo || '',
          footer_address: data.footer_address || '',
          footer_phone1: data.footer_phone1 || '',
          footer_phone2: data.footer_phone2 || '',
          footer_email: data.footer_email || '',
          footer_facebook: data.footer_facebook || '',
          footer_instagram: data.footer_instagram || '',
          footer_twitter: data.footer_twitter || '',
          footer_whatsapp: data.footer_whatsapp || '',
          footer_copyright: data.footer_copyright || '',
          // Branch addresses
          branch_fes1_name: data.branch_fes1_name || '',
          branch_fes1_address: data.branch_fes1_address || '',
          branch_fes1_phone: data.branch_fes1_phone || '',
          branch_fes2_name: data.branch_fes2_name || '',
          branch_fes2_address: data.branch_fes2_address || '',
          branch_fes2_phone: data.branch_fes2_phone || '',
          branch_casa_name: data.branch_casa_name || '',
          branch_casa_address: data.branch_casa_address || '',
          branch_casa_phone: data.branch_casa_phone || '',
          footer_phone_fes: data.footer_phone_fes || '',
          footer_phone_casa: data.footer_phone_casa || '',
          footer_email_fes: data.footer_email_fes || '',
          footer_email_casa: data.footer_email_casa || '',
        });
        
        // Set footer links
        setFooterInfoLinks(data.footer_info_links || []);
        setFooterSupportLinks(data.footer_support_links || []);
        setFooterAccountLinks(data.footer_account_links || []);
        setFooterGroceryLinks(data.footer_grocery_links || []);
      }
    } catch (err) {
      setError('Erreur lors du chargement des paramètres');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const allSettings = {
        ...headerSettings,
        ...footerSettings,
        footer_info_links: footerInfoLinks,
        footer_support_links: footerSupportLinks,
        footer_account_links: footerAccountLinks,
        footer_grocery_links: footerGroceryLinks,
      };

      const response = await settingsRepository.update(allSettings);
      if (response.success) {
        setSuccess('Paramètres enregistrés avec succès!');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Erreur lors de l\'enregistrement');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const response = await settingsRepository.uploadLogo(file, type);
      if (response.success) {
        if (type === 'header') {
          setHeaderSettings(prev => ({ ...prev, header_logo: response.data.url }));
        } else if (type === 'header_black') {
          setHeaderSettings(prev => ({ ...prev, header_logo_black: response.data.url }));
        } else {
          setFooterSettings(prev => ({ ...prev, footer_logo: response.data.url }));
        }
        setSuccess('Logo téléchargé avec succès!');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Logo upload error:', err);
      setError('Erreur lors du téléchargement du logo: ' + (err.message || 'Unknown error'));
      console.error(err);
    }
  };

  const updateLink = (links, setLinks, index, field, value) => {
    const newLinks = [...links];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setLinks(newLinks);
  };

  const addLink = (links, setLinks) => {
    setLinks([...links, { title: '', url: '' }]);
  };

  const removeLink = (links, setLinks, index) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const renderLinksEditor = (title, links, setLinks) => (
    <Card className="mb-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">{title}</h6>
          <Button variant="success" size="sm" onClick={() => addLink(links, setLinks)}>
            <i className="ri-add-line me-1"></i> Ajouter
          </Button>
        </div>
        {links.map((link, index) => (
          <Row key={index} className="mb-2">
            <Col md={5}>
              <Form.Control
                type="text"
                placeholder="Titre"
                value={link.title}
                onChange={(e) => updateLink(links, setLinks, index, 'title', e.target.value)}
              />
            </Col>
            <Col md={5}>
              <Form.Control
                type="text"
                placeholder="URL"
                value={link.url}
                onChange={(e) => updateLink(links, setLinks, index, 'url', e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Button variant="danger" size="sm" onClick={() => removeLink(links, setLinks, index)}>
                <i className="ri-delete-bin-line"></i>
              </Button>
            </Col>
          </Row>
        ))}
      </CardBody>
    </Card>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Paramètres du Site</h4>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner size="sm" className="me-2" />
              Enregistrement...
            </>
          ) : (
            <>
              <i className="ri-save-line me-2"></i>
              Enregistrer
            </>
          )}
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Card>
          <Card.Header>
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="header">
                  <i className="ri-layout-top-line me-2"></i>
                  Header
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="footer">
                  <i className="ri-layout-bottom-line me-2"></i>
                  Footer
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          <CardBody>
            <Tab.Content>
              {/* Header Tab */}
              <Tab.Pane eventKey="header">
                <Row>
                  <Col md={6}>
                    <Card className="mb-3">
                      <CardBody>
                        <h6 className="mb-3">Logo (Version Claire)</h6>
                        <p className="text-muted small">Logo principal pour le header</p>
                        <div className="mb-3">
                          {headerSettings.header_logo && (
                            <img 
                              src={headerSettings.header_logo.startsWith('http') ? headerSettings.header_logo : headerSettings.header_logo}
                              alt="Header Logo" 
                              style={{ maxHeight: '60px', marginBottom: '10px' }}
                            />
                          )}
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleLogoUpload(e, 'header')}
                          />
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="mb-3">
                      <CardBody>
                        <h6 className="mb-3">Logo (Version Noire/Sombre)</h6>
                        <p className="text-muted small">Logo pour sidebar et version mobile</p>
                        <div className="mb-3">
                          {headerSettings.header_logo_black && (
                            <img 
                              src={headerSettings.header_logo_black.startsWith('http') ? headerSettings.header_logo_black : headerSettings.header_logo_black}
                              alt="Header Logo Black" 
                              style={{ maxHeight: '60px', marginBottom: '10px', backgroundColor: '#f5f5f5', padding: '5px' }}
                            />
                          )}
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleLogoUpload(e, 'header_black')}
                          />
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Card className="mb-3">
                      <CardBody>
                        <h6 className="mb-3">Numéros de Téléphone</h6>
                        <Form.Group className="mb-3">
                          <Form.Label>Téléphone 1</Form.Label>
                          <Form.Control
                            type="text"
                            value={headerSettings.header_phone1}
                            onChange={(e) => setHeaderSettings(prev => ({ ...prev, header_phone1: e.target.value }))}
                            placeholder="06 XX XX XX XX"
                          />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Téléphone 2</Form.Label>
                          <Form.Control
                            type="text"
                            value={headerSettings.header_phone2}
                            onChange={(e) => setHeaderSettings(prev => ({ ...prev, header_phone2: e.target.value }))}
                            placeholder="06 XX XX XX XX"
                          />
                        </Form.Group>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </Tab.Pane>

              {/* Footer Tab */}
              <Tab.Pane eventKey="footer">
                <Row>
                  <Col md={6}>
                    <Card className="mb-3">
                      <CardBody>
                        <h6 className="mb-3">Logo Footer</h6>
                        <div className="mb-3">
                          {footerSettings.footer_logo && (
                            <img 
                              src={footerSettings.footer_logo.startsWith('http') ? footerSettings.footer_logo : footerSettings.footer_logo}
                              alt="Footer Logo" 
                              style={{ maxHeight: '60px', marginBottom: '10px' }}
                            />
                          )}
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleLogoUpload(e, 'footer')}
                          />
                        </div>
                      </CardBody>
                    </Card>

                    <Card className="mb-3">
                      <CardBody>
                        <h6 className="mb-3">Informations de Contact (Général)</h6>
                        <Form.Group className="mb-0">
                          <Form.Label>Adresse (Par défaut)</Form.Label>
                          <Form.Control
                            type="text"
                            value={footerSettings.footer_address}
                            onChange={(e) => setFooterSettings(prev => ({ ...prev, footer_address: e.target.value }))}
                            placeholder="Adresse principale"
                          />
                        </Form.Group>
                      </CardBody>
                    </Card>
                    
                    <Card className="mb-3 border-success">
                      <CardBody>
                        <h6 className="mb-3 text-success">
                          <i className="ri-map-pin-line me-2"></i>
                          Adresses & Contact - Région Fès
                        </h6>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>📍 Adresse Fès 1</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="ex: 36 Avenue Ibn Khatib, Fès"
                            value={footerSettings.branch_fes1_address}
                            onChange={(e) => setFooterSettings(prev => ({ ...prev, branch_fes1_address: e.target.value }))}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>📍 Adresse Fès 2</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="ex: 123 Boulevard Hassan II, Fès"
                            value={footerSettings.branch_fes2_address}
                            onChange={(e) => setFooterSettings(prev => ({ ...prev, branch_fes2_address: e.target.value }))}
                          />
                        </Form.Group>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>📞 Téléphone Fès</Form.Label>
                              <Form.Control
                                type="text"
                                value={footerSettings.footer_phone_fes}
                                onChange={(e) => setFooterSettings(prev => ({ ...prev, footer_phone_fes: e.target.value }))}
                                placeholder="05 35 XX XX XX"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>✉️ Email Fès</Form.Label>
                              <Form.Control
                                type="email"
                                value={footerSettings.footer_email_fes}
                                onChange={(e) => setFooterSettings(prev => ({ ...prev, footer_email_fes: e.target.value }))}
                                placeholder="fes@example.com"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    <Card className="mb-3 border-warning">
                      <CardBody>
                        <h6 className="mb-3 text-warning">
                          <i className="ri-map-pin-line me-2"></i>
                          Adresse & Contact - Région Casablanca
                        </h6>
                        
                        <Form.Group className="mb-3">
                          <Form.Label>📍 Adresse Casablanca</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="ex: 45 Rue Mohamed V, Casablanca"
                            value={footerSettings.branch_casa_address}
                            onChange={(e) => setFooterSettings(prev => ({ ...prev, branch_casa_address: e.target.value }))}
                          />
                        </Form.Group>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>📞 Téléphone Casa</Form.Label>
                              <Form.Control
                                type="text"
                                value={footerSettings.footer_phone_casa}
                                onChange={(e) => setFooterSettings(prev => ({ ...prev, footer_phone_casa: e.target.value }))}
                                placeholder="05 22 XX XX XX"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>✉️ Email Casa</Form.Label>
                              <Form.Control
                                type="email"
                                value={footerSettings.footer_email_casa}
                                onChange={(e) => setFooterSettings(prev => ({ ...prev, footer_email_casa: e.target.value }))}
                                placeholder="casa@example.com"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    <Card className="mb-3">
                      <CardBody>
                        <h6 className="mb-3">Réseaux Sociaux</h6>
                        <Form.Group className="mb-3">
                          <Form.Label>Facebook</Form.Label>
                          <Form.Control
                            type="url"
                            value={footerSettings.footer_facebook}
                            onChange={(e) => setFooterSettings(prev => ({ ...prev, footer_facebook: e.target.value }))}
                          />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>Instagram</Form.Label>
                          <Form.Control
                            type="url"
                            value={footerSettings.footer_instagram}
                            onChange={(e) => setFooterSettings(prev => ({ ...prev, footer_instagram: e.target.value }))}
                          />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>Twitter</Form.Label>
                          <Form.Control
                            type="url"
                            value={footerSettings.footer_twitter}
                            onChange={(e) => setFooterSettings(prev => ({ ...prev, footer_twitter: e.target.value }))}
                          />
                        </Form.Group>
                        <Form.Group className="mb-3">
                          <Form.Label>WhatsApp</Form.Label>
                          <Form.Control
                            type="url"
                            value={footerSettings.footer_whatsapp}
                            onChange={(e) => setFooterSettings(prev => ({ ...prev, footer_whatsapp: e.target.value }))}
                            placeholder="https://wa.me/212XXXXXXXXX"
                          />
                        </Form.Group>
                      </CardBody>
                    </Card>

                    <Card className="mb-3">
                      <CardBody>
                        <h6 className="mb-3">Copyright</h6>
                        <Form.Control
                          type="text"
                          value={footerSettings.footer_copyright}
                          onChange={(e) => setFooterSettings(prev => ({ ...prev, footer_copyright: e.target.value }))}
                        />
                      </CardBody>
                    </Card>
                  </Col>

                  <Col md={6}>
                    {renderLinksEditor('Information', footerInfoLinks, setFooterInfoLinks)}
                    {renderLinksEditor('Support Client', footerSupportLinks, setFooterSupportLinks)}
                    {renderLinksEditor('Mon Compte', footerAccountLinks, setFooterAccountLinks)}
                    {renderLinksEditor('Épicerie Quotidienne', footerGroceryLinks, setFooterGroceryLinks)}
                  </Col>
                </Row>
              </Tab.Pane>
            </Tab.Content>
          </CardBody>
        </Card>
      </Tab.Container>
    </div>
  );
};

export default Settings;
